import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Card, PageHeader, Btn, Input, Select, EmptyState } from "../components/UI";
import { Layers, Plus, Trash2, Rocket, Loader2, Save, CalendarRange, Wallet, Target } from "lucide-react";
import { toast } from "sonner";

const CHANNEL_COLORS = {
  "Connected TV": "bg-emerald-400", "Online video": "bg-cyan-400", "Display": "bg-blue-400",
  "Streaming audio": "bg-fuchsia-400", "Native": "bg-amber-400",
};

const MixBar = ({ channels }) => (
  <div>
    <div className="flex h-2 rounded-full overflow-hidden bg-white/5">
      {Object.entries(channels).map(([ch, pct]) => (
        <div key={ch} className={`${CHANNEL_COLORS[ch] || "bg-slate-400"}`} style={{ width: `${pct}%` }} title={`${ch} ${pct}%`} />
      ))}
    </div>
    <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2">
      {Object.entries(channels).map(([ch, pct]) => (
        <span key={ch} className="inline-flex items-center gap-1.5 text-[10px] text-slate-400">
          <span className={`w-2 h-2 rounded-full ${CHANNEL_COLORS[ch] || "bg-slate-400"}`} /> {ch} <span className="text-white font-mono">{pct}%</span>
        </span>
      ))}
    </div>
  </div>
);

export default function MediaPlans() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [sourceCampaign, setSourceCampaign] = useState("");
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(null);
  const [applyForm, setApplyForm] = useState({ name: "", total_budget: "", start_date: "" });
  const [busyApply, setBusyApply] = useState(false);

  const load = async () => {
    const [p, c] = await Promise.all([api.get("/media-plans"), api.get("/campaigns")]);
    setPlans(p.data);
    setCampaigns(c.data);
  };
  useEffect(() => { load(); }, []);

  const saveFromCampaign = async () => {
    if (!sourceCampaign) return;
    setSaving(true);
    try {
      await api.post(`/media-plans/from-campaign/${sourceCampaign}`);
      toast.success("Media plan template saved");
      setSourceCampaign("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save template");
    } finally { setSaving(false); }
  };

  const apply = async () => {
    setBusyApply(true);
    try {
      const { data } = await api.post(`/media-plans/${applying.id}/apply`, {
        name: applyForm.name || null,
        total_budget: applyForm.total_budget ? parseFloat(applyForm.total_budget) : null,
        start_date: applyForm.start_date || null,
      });
      toast.success(`Draft campaign "${data.name}" created`);
      navigate(`/campaigns/${data.campaign_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not apply plan");
    } finally { setBusyApply(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this media plan template?")) return;
    await api.delete(`/media-plans/${id}`);
    load();
  };

  if (!plans) return <div className="p-8 text-slate-400 text-sm">Loading media plans…</div>;

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="media-plans-page">
      <PageHeader
        eyebrow="Advertising"
        title="Media Plans"
        subtitle="Reusable channel-mix templates — apply one to spin up a ready-made draft campaign, or save any campaign as a template."
      />

      <Card data-testid="save-from-campaign-card">
        <div className="flex flex-col md:flex-row md:items-end gap-3">
          <div className="flex-1">
            <Select label="Save an existing campaign as a template" value={sourceCampaign} onChange={(e) => setSourceCampaign(e.target.value)} data-testid="source-campaign-select">
              <option value="">Choose a campaign…</option>
              {campaigns.map((c) => <option key={c.id} value={c.id}>{`${c.name} — $${Number(c.total_budget || 0).toLocaleString()}`}</option>)}
            </Select>
          </div>
          <Btn onClick={saveFromCampaign} disabled={!sourceCampaign || saving} data-testid="save-template-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save as template
          </Btn>
        </div>
      </Card>

      {plans.length === 0 ? (
        <EmptyState icon={Layers} title="No media plans yet" description="Save a campaign as a template to get started." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="media-plans-grid">
          {plans.map((p) => (
            <Card key={p.id} className="flex flex-col" data-testid={`media-plan-${p.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-wider text-cyan-300 font-semibold">{p.is_starter ? "Starter template" : "Saved template"} · {p.objective}</div>
                  <div className="font-display text-lg font-bold mt-0.5">{p.name}</div>
                </div>
                {!p.is_starter && (
                  <button onClick={() => del(p.id)} className="text-slate-500 hover:text-rose-300 p-1 shrink-0" data-testid={`delete-plan-${p.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
              {p.description && <p className="text-xs text-slate-400 mt-1.5">{p.description}</p>}
              <div className="mt-4"><MixBar channels={p.channels || {}} /></div>
              <div className="mt-4 flex items-center gap-4 text-xs text-slate-400">
                <span className="inline-flex items-center gap-1"><Wallet className="w-3 h-3" /> ${Number(p.default_budget || 0).toLocaleString()}</span>
                <span className="inline-flex items-center gap-1"><CalendarRange className="w-3 h-3" /> {p.duration_days} days</span>
                <span className="inline-flex items-center gap-1"><Target className="w-3 h-3" /> {Object.keys(p.channels || {}).length} channels</span>
              </div>
              <div className="mt-4 pt-3 border-t border-white/10 flex justify-end">
                <Btn onClick={() => { setApplying(p); setApplyForm({ name: "", total_budget: p.default_budget, start_date: "" }); }} data-testid={`apply-plan-${p.id}`}>
                  <Rocket className="w-4 h-4" /> Use this plan
                </Btn>
              </div>
            </Card>
          ))}
        </div>
      )}

      {applying && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setApplying(null)}>
          <div className="bg-app border border-white/10 rounded-lg max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="apply-plan-modal">
            <h3 className="font-display text-xl font-bold">Use "{applying.name}"</h3>
            <p className="text-xs text-slate-500 mt-1">Creates a draft campaign with this channel mix — you can refine everything in the builder.</p>
            <div className="mt-4 space-y-3">
              <Input label="Campaign name (optional)" value={applyForm.name} onChange={(e) => setApplyForm({ ...applyForm, name: e.target.value })} placeholder={`${applying.name} — today`} data-testid="apply-name-input" />
              <Input label="Total budget (USD)" type="number" value={applyForm.total_budget} onChange={(e) => setApplyForm({ ...applyForm, total_budget: e.target.value })} data-testid="apply-budget-input" />
              <Input label="Start date (optional — defaults to today)" type="date" value={applyForm.start_date} onChange={(e) => setApplyForm({ ...applyForm, start_date: e.target.value })} hint={`Runs ${applying.duration_days} days from start`} data-testid="apply-start-input" />
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <Btn variant="secondary" onClick={() => setApplying(null)}>Cancel</Btn>
              <Btn onClick={apply} disabled={busyApply} data-testid="apply-confirm-btn">
                {busyApply ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create draft campaign
              </Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
